import { timeUtils } from '@sato/components';
import { UseQueryOptions } from '@tanstack/react-query';

import { api } from '@api';
import {
  WaterUsageAmounts,
  WaterUsageAmountsAPI,
  WaterUsageAPIResponse,
  WaterUsageData,
  WaterUsageUnit,
} from '@models/waterUsage.types';

const deserializeUsageAmounts = (
  usage: WaterUsageAmountsAPI
): WaterUsageAmounts => ({
  ...usage,
  timeStampWarm: usage.timeStampWarm
    ? new Date(usage.timeStampWarm)
    : undefined,
  timeStampCold: usage.timeStampCold
    ? new Date(usage.timeStampCold)
    : undefined,
  previousTimeStampWarm: usage.previousTimeStampWarm
    ? new Date(usage.previousTimeStampWarm)
    : undefined,
  previousTimeStampCold: usage.previousTimeStampCold
    ? new Date(usage.previousTimeStampCold)
    : undefined,
});

export const deserializeMeasurements = (
  data: WaterUsageAPIResponse
): WaterUsageData => ({
  ...data,
  updated: new Date(data.updated),
  summary: deserializeUsageAmounts(data.summary),
  period: {
    start: new Date(data.period.start),
    end: new Date(data.period.end),
  },
  items: data.items.map((measurement) => ({
    ...measurement,
    date: new Date(measurement.date),
    timeStamp: new Date(measurement.timeStamp),
    previousTimestamp: measurement.previousTimestamp
      ? new Date(measurement.previousTimestamp)
      : undefined,
    usage: deserializeUsageAmounts(measurement.usage),
  })),
});

const DAILY_MEASUREMENTS_KEY = 'FETCH_WATER_USAGE_DAILY_MEASUREMENTS';

export const getWaterUsageRangeQueryKey = (apartmentId: string) => [
  DAILY_MEASUREMENTS_KEY,
  apartmentId,
];

const getDailyMeasurementsByMonthQueryKey = (
  apartmentId: string | null,
  selectedDate: Date | undefined
) => [
  DAILY_MEASUREMENTS_KEY,
  apartmentId,
  selectedDate
    ? timeUtils.formatDate({
        date: selectedDate,
        // measurements are by-month basis, so query key should be only month and year
        dateFormat: 'M y',
      })
    : '',
];

/** Gets water usage of the selected month */
export const getDailyMeasurementsByMonthQuery = ({
  apartmentId,
  selectedDate,
}: {
  apartmentId: string | null;
  selectedDate: Date | undefined;
}) =>
  ({
    queryKey: getDailyMeasurementsByMonthQueryKey(apartmentId, selectedDate),
    queryFn: async () => {
      if (!apartmentId || !selectedDate) {
        throw Error(
          '[dailyMeasurementsByMonthQuery]: apartmentId and selectedDate is required'
        );
      }

      const { status, data } = await api.measurements.getWaterUsage({
        apartmentId,
        month: (selectedDate.getMonth() + 1).toString(),
        year: selectedDate.getFullYear().toString(),
        unit: WaterUsageUnit.LITER,
      });

      if (status !== 200 || !data.items) {
        throw Error('Error fetching water usage data');
      }

      return data;
    },
  }) satisfies UseQueryOptions;

/**
 * Query for getting the range of time where water usage data is available.
 * If this results in an error, we can assume that the selected apartmentId
 * doesn't have water usage data.
 */
export const fetchWaterUsageRange = async (apartmentId: string | null) => {
  if (!apartmentId) {
    throw Error('[waterUsageRangeQuery]: apartmentId is required');
  }

  const { status, data } =
    await api.measurements.getWaterUsageRange(apartmentId);

  if (status !== 200 || !data.period) {
    return undefined;
  }

  return data.period;
};
